import React from 'react'
import AddToExistingFamily, { AddToExistingFamilyFormData } from './AddToExistingFamily'
import ClashDetectionModal from './ClashDetectionModal'
import CreateNewFamily, { CreateNewFamilyFormData } from './CreateNewFamily'
import ManageUnmappedProductDialog from './ManageUnmappedProductDialog'

/**
 * Represents the different steps in the manage unmapped product workflow
 */
export type StepType =
    | 'initial'
    | 'createNewFamily'
    | 'addToExistingFamily'
    | 'clashDetectionNewFamily'
    | 'clashDetectionExistingFamily'

/**
 * Result data returned when the manage unmapped product workflow is completed
 */
export interface ManageUnmappedProductWizardResult {
    /** The type of operation performed */
    type: 'createNewFamily' | 'addToExistingFamily'
    /** The form data submitted by the user */
    data: CreateNewFamilyFormData | AddToExistingFamilyFormData
}

/**
 * Props for the ManageUnmappedProductWizard component
 */
export interface ManageUnmappedProductWizardProps {
    open: boolean
    onClose: () => void
    /** Callback when the workflow is completed with result data */
    onComplete: (result: ManageUnmappedProductWizardResult) => void
    /** Callback when an error occurs during the workflow */
    onError?: (error: Error) => void
    /** Optional name of the product being managed */
    productName?: string
}

interface StepState {
    currentStep: StepType
    formData: {
        initialChoice?: string
        createNewFamily?: CreateNewFamilyFormData
        addToExistingFamily?: AddToExistingFamilyFormData
    }
}

/**
 * Main orchestrator component for managing unmapped products. This is AE only and won't be available in BPM side. 
 * 
 * This component provides a multi-step workflow that allows users to either:
 * 1. Create a new family for an unmapped product
 * 2. Add the product to an existing family
 * 
 * The workflow includes clash detection and form validation at each step.
 * 
 * @param props - The component props
 * @returns The ManageUnmappedProductWizard component
 */
const ManageUnmappedProductWizard: React.FC<ManageUnmappedProductWizardProps> = ({
    open,
    onClose,
    onComplete,
    onError,
    productName,
}) => {
    const [state, setState] = React.useState<StepState>({
        currentStep: 'initial',
        formData: {},
    })

    const handleInitialChoice = React.useCallback((selectedOption: string) => {
        if (selectedOption === 'new') {
            setState(prev => ({
                ...prev,
                currentStep: 'createNewFamily',
                formData: {
                    ...prev.formData,
                    initialChoice: selectedOption,
                },
            }))
        } else {
            setState(prev => ({
                ...prev,
                currentStep: 'addToExistingFamily',
                formData: {
                    ...prev.formData,
                    initialChoice: selectedOption,
                },
            }))
        }
    }, [])

    const handleCreateNewFamilySave = React.useCallback((formData: CreateNewFamilyFormData) => {
        setState(prev => ({
            ...prev,
            currentStep: 'clashDetectionNewFamily',
            formData: {
                ...prev.formData,
                createNewFamily: formData,
            },
        }))

        
        try {
            onComplete({
                type: 'createNewFamily',
                data: formData,
            })
            // Close the dialog and reset state
            setState({
                currentStep: 'initial',
                formData: {},
            })
            onClose()
        } catch (error) {
            // Reset to the form step to allow retry
            setState(prev => ({
                ...prev,
                currentStep: 'createNewFamily',
            }))

            onError?.(error as Error)
        }

    }, [onComplete, onError, onClose])

    const handleAddToExistingFamilySave = React.useCallback((formData: AddToExistingFamilyFormData) => {
        setState(prev => ({
            ...prev,
            currentStep: 'clashDetectionExistingFamily',
            formData: {
                ...prev.formData,
                addToExistingFamily: formData,
            },
        }))

        // Simulate clash detection process with potential error
     
            try {
                onComplete({
                    type: 'addToExistingFamily',
                    data: formData,
                })
                // Close the dialog and reset state
                setState({
                    currentStep: 'initial',
                    formData: {},
                })
                onClose()
            } catch (error) {
                // Reset to the form step to allow retry
                setState(prev => ({
                    ...prev,
                    currentStep: 'addToExistingFamily',
                }))

                onError?.(error as Error)

            }
    }, [onComplete, onError, onClose])

    const handleClose = React.useCallback(() => {
        setState({
            currentStep: 'initial',
            formData: {},
        })
        onClose()
    }, [onClose])

    const handleBackToInitial = React.useCallback(() => {
        setState(prev => ({
            ...prev,
            currentStep: 'initial',
        }))
    }, [])

    React.useEffect(() => {
        if (!open) {
            setState({
                currentStep: 'initial',
                formData: {},
            })
        }
    }, [open])

    return (
        <>
            <ManageUnmappedProductDialog
                open={open && state.currentStep === 'initial'}
                onClose={handleClose}
                onSaveAndContinue={handleInitialChoice}
                productName={productName}
            />

            <CreateNewFamily
                open={open && state.currentStep === 'createNewFamily'}
                onClose={handleBackToInitial}
                onSaveAndContinue={handleCreateNewFamilySave}
            />

            <AddToExistingFamily
                open={open && state.currentStep === 'addToExistingFamily'}
                onClose={handleBackToInitial}
                onSaveAndContinue={handleAddToExistingFamilySave}
            />

            <ClashDetectionModal
                open={open && (
                    state.currentStep === 'clashDetectionNewFamily' ||
                    state.currentStep === 'clashDetectionExistingFamily'
                )}
                title="Manage Unmapped Product"
                subtitle={
                    state.currentStep === 'clashDetectionNewFamily'
                        ? 'Create a New Family'
                        : 'Add to Existing Family'
                }
                progressText="Checking for Clashes"
            />
        </>
    )
}

export default ManageUnmappedProductWizard